import { useCartMinimums } from '../../utils/useCartMinimums'
import React, { useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useAppNavigate } from '../../context/AppContext'
import { IProduct } from '../../types/products'
import CartPageItem from './cart-page-item/CartPageItem'
import { Button } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { useDeviceDetect } from '../../utils/use-device-detect'
import CartPageItemMobile from './cart-page-item/cart-page-item-mobile/CartPageItemMobile'
import { useGetProductsQuery } from '../../store/api/productsApi'
import Logo from '../../assets/images/logo.png'
import Loading from '../../assets/icons/loading.svg?react'
import './CartPage.scss'
import CartMenuItem from '../../components/cart-menu-item/CartMenuItem'
import { PandaSign } from '../../components/panda-sign/PandaSign'
import ViewRouletteWheel from './ViewRouletteWheel'
import { getStorageKey } from '../../utils/appType'

const c = 'cart-page'

const CartPage = () => {
  const navigate = useAppNavigate()
  const { t } = useTranslation()
  const { cart, setCart } = useOutletContext<{
    cart: IProduct[]
    setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
  }>()
  const { checkedCart, blocked, hasMinimumErrors } = useCartMinimums(cart)
  const [openWheel, setOpenWheel] = useState(false)
  const { isMobile } = useDeviceDetect()
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const generalInfo = JSON.parse(
    localStorage.getItem(getStorageKey('info')) || '{}',
  )

  const { data: productsData = [], isLoading: loadingProducts } =
    useGetProductsQuery()

  const additionalProducts = useMemo(() => {
    return productsData
      .map((item: { items: IProduct[] }) =>
        item.items.filter((product: IProduct) => product.isAdditional),
      )
      .flat()
  }, [productsData])

  useEffect(() => {
    const targetTimezone = 'Europe/Chisinau'

    const targetTimeBefore = DateTime.now()
      .setZone(targetTimezone)
      .set({
        hour: Number(generalInfo?.minTime?.split(':')[0] || 10),
        minute: Number(generalInfo?.minTime?.split(':')[1] || 0),
        second: 0,
      })

    const targetTimeAfter = DateTime.now()
      .setZone(targetTimezone)
      .set({
        hour: Number(generalInfo?.maxTime?.split(':')[0] || 23),
        minute: Number(generalInfo?.maxTime?.split(':')[1] || 0),
        second: 0,
      })

    const currentTime = DateTime.now().setZone(targetTimezone)

    const isBefore10AM = currentTime < targetTimeBefore
    const isAfter11PM = currentTime > targetTimeAfter

    setIsButtonDisabled(isBefore10AM || isAfter11PM)
  }, [])

  function disableCart() {
    if (generalInfo.dayOff) {
      return true
    }

    if (isButtonDisabled) {
      return true
    }

    if (!cart.length) {
      return true
    }

    if (Number(totalPrice) < Number(generalInfo?.minPrice)) {
      return true
    }

    return false
  }

  const totalPrice = cart.reduce(
    (total: number, item: IProduct) =>
      total + Number(item.price) * item.quantity!,
    0,
  )

  // Calculate progress percentage for the progress bar
  const progressPercentage = Math.min(
    (totalPrice / (generalInfo?.rouletteMinPrice || 1)) * 100,
    100,
  )

  useEffect(() => {
    scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const updatedAdditionalProducts = additionalProducts.filter(
    (additionalProduct: IProduct) => {
      const existsInCart = cart.some(
        (cartItem: IProduct) => cartItem._id === additionalProduct._id,
      )

      if (existsInCart) {
        return false
      } else {
        return true
      }
    },
  )

  return (
    <div className={c}>
      <div className={`${c}__container`}>
        <div className={`${c}__container-title`}>{t('CART')}</div>
        <div className={`${c}__container-products`}>
          {cart.length ? (
            checkedCart.map((item: IProduct) =>
              isMobile ? (
                <CartPageItemMobile
                  data={item}
                  key={item._id}
                  setCart={setCart}
                />
              ) : (
                <CartPageItem data={item} key={item._id} setCart={setCart} />
              ),
            )
          ) : (
            <div className={`${c}__container-products-no_data`}>
              {t('CART_IS_EMPTY')}
            </div>
          )}
        </div>
        {!!cart.length && (
          <React.Fragment>
            {loadingProducts ? (
              <Loading />
            ) : additionalProducts.length ? (
              <div className={`${c}__container-additions`}>
                <div className={`${c}__container-additions-title`}>
                  {t('SOMETHING_ELSE')}
                </div>
                <div className={`${c}__container-additions-content`}>
                  {additionalProducts.map((product: IProduct) => {
                    return (
                      <div
                        key={product._id}
                        className={`${c}__container-additions-content-item`}
                      >
                        <CartMenuItem
                          data={product}
                          cart={cart}
                          setCart={setCart}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : null}
            {isMobile && generalInfo.cartPromoMessage && (
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: '30px',
                  height: 'auto',
                }}
              >
                <PandaSign message={generalInfo.cartPromoMessage} />
              </div>
            )}
            <div className={`${c}__container-footer`}>
              <div className={`${c}__container-footer-total`}>
                <div className={`${c}__container-footer-total-item`}>
                  <div className={`${c}__container-footer-total-item-label`}>
                    {t('ORDER_SUM')}
                  </div>
                  <div className={`${c}__container-footer-total-item-value`}>
                    {totalPrice} lei
                  </div>
                </div>

                <div className={`${c}__container-footer-total-checkout`}>
                  <Button
                    variant='contained'
                    disabled={disableCart() || blocked}
                    onClick={() => navigate('checkout')}
                    sx={{
                      backgroundColor: 'var(--color-primary)',
                      textTransform: 'none',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      width: '100%',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-hover)',
                      },
                    }}
                  >
                    {t('FINALIZE_ORDER')}
                  </Button>
                  {hasMinimumErrors && (
                    <div className='minimum-quantity minimum-quantity--error' role='alert'>
                      {t('CART_MINIMUM_ERROR')}
                    </div>
                  )}
                  {Number(totalPrice) < Number(generalInfo?.minPrice) && (
                    <div className={`${c}-note`}>
                      <Trans
                        i18nKey='MINIMUM_PRICE'
                        values={{ minPrice: generalInfo?.minPrice }}
                      />
                    </div>
                  )}
                  {generalInfo?.rouletteMinPrice &&
                    generalInfo.isRouletteActive && (
                      <div className={`${c}__container-footer-total-progress`}>
                        <div
                          className={`${c}__container-footer-total-progress-bar-container`}
                        >
                          <div
                            className={`${c}__container-footer-total-progress-bar`}
                            style={{ width: `${progressPercentage}%` }}
                          >
                            <div
                              className={`${c}__container-footer-total-progress-price`}
                            >
                              <b>{totalPrice}</b> /{' '}
                              <b>{generalInfo.rouletteMinPrice}</b> lei
                            </div>
                            {progressPercentage > 5 && (
                              <div
                                className={`${c}__container-footer-total-progress-panda`}
                              >
                                <img
                                  src={Logo}
                                  alt='panda'
                                  className={`${c}__container-footer-total-progress-panda`}
                                  width={25}
                                  height={25}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div
                          className={`${c}__container-footer-total-progress-label`}
                        >
                          {t('PROGRESS_TO_ROULETTE')}
                          <span
                            className={`${c}__container-footer-total-progress-label-link`}
                            onClick={() => setOpenWheel(true)}
                          >
                            Vezi premiile ruletei
                          </span>
                        </div>
                      </div>
                    )}
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: '30px',
                      height: 'auto',
                    }}
                  >
                    {generalInfo.cartPromoMessage && !isMobile && (
                      <PandaSign
                        message={generalInfo.cartPromoMessage}
                        style={{
                          position: 'absolute',
                          top: '80px',
                          left: '50px',
                        }}
                      />
                    )}
                  </div>
                </div>
                {!generalInfo?.dayOff && isButtonDisabled && (
                  <div className={`${c}-note-time_limit`}>
                    <Trans
                      i18nKey='TIME_LIMIT'
                      values={{
                        minTime: generalInfo?.minTime,
                        maxTime: generalInfo.maxTime,
                      }}
                    />
                  </div>
                )}
                {generalInfo?.dayOff && (
                  <div className={`${c}-note-time_limit`}>
                    <Trans i18nKey='DAY_OFF' />
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
      {openWheel && <ViewRouletteWheel setOpen={setOpenWheel} />}
    </div>
  )
}

export default CartPage
